import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Building2,
  Briefcase,
  Layers,
  TrendingUp,
  Globe,
  Star,
  Loader2,
  AlertCircle,
} from 'lucide-react';

/**
 * Issuer Profile screen — Path B Task #14, Option A.
 *
 * Drill-down target from SecurityManagement (issuer cell) and Holdings.
 * Renders an issuer header + a "Held in" pillar strip showing the issuer's
 * footprint across Public FI, Credit & Loans, and Alternatives.
 *
 * Backed by /api/credit/issuers/{id}/holdings-by-pillar.
 *
 * Anti-scope: read-only. No editing, no trade entry — drill-down only.
 */

interface PillarBreakdown {
  instrumentCount: number;
  marketValue: number;
}

interface PillarSummary {
  portfolioCount?: number | null;
  instrumentCount: number;
  marketValue: number;
  byAssetClass?: Array<{ assetClass: string; instrumentCount: number; marketValue: number }>;
  breakdown?: Record<string, PillarBreakdown>;
}

interface IssuerHeader {
  id: number;
  issuerName: string;
  sector?: string;
  industry?: string;
  country?: string;
  creditRating?: string;
}

interface HoldingsByPillarResponse {
  issuer: IssuerHeader;
  publicFI: PillarSummary;
  creditAndLoans: PillarSummary;
  alternatives: PillarSummary;
}

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.85rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
  color: #94a3b8;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: rgba(59, 130, 246, 0.45);
    background: rgba(59, 130, 246, 0.1);
    color: #93c5fd;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 1.75rem 2rem;
  margin-bottom: 1.5rem;
`;

const IssuerName = styled.h1`
  color: white;
  font-size: 1.65rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    width: 22px;
    height: 22px;
    color: #93c5fd;
  }
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 0.75rem;
`;

const MetaItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.82rem;
  color: #94a3b8;

  svg {
    width: 14px;
    height: 14px;
    opacity: 0.7;
  }

  strong {
    color: #e2e8f0;
    font-weight: 600;
  }
`;

const SectionTitle = styled.h2`
  color: white;
  font-size: 1.05rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 18px;
    height: 18px;
    color: #93c5fd;
  }
`;

const PillarStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.75rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const PillarCard = styled(motion.div)<{ $accent: string; $hasData: boolean }>`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border: 1px solid ${(p) => (p.$hasData ? `${p.$accent}55` : 'rgba(255, 255, 255, 0.06)')};
  border-radius: 14px;
  padding: 1.5rem;
  border-left: 4px solid ${(p) => (p.$hasData ? p.$accent : 'rgba(148, 163, 184, 0.25)')};
  opacity: ${(p) => (p.$hasData ? 1 : 0.55)};
`;

const PillarHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.85rem;
`;

const PillarIcon = styled.div<{ $color: string }>`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${(p) => `${p.$color}1f`};
  border: 1px solid ${(p) => `${p.$color}3a`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => p.$color};

  svg {
    width: 18px;
    height: 18px;
  }
`;

const PillarLabel = styled.div`
  color: #cbd5e1;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const PillarValue = styled.div`
  color: white;
  font-size: 1.6rem;
  font-weight: 700;
  margin-bottom: 0.4rem;
`;

const PillarMeta = styled.div`
  color: #94a3b8;
  font-size: 0.78rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;

  span strong {
    color: #e2e8f0;
    font-weight: 600;
  }
`;

const Detail = styled.div`
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.25rem;
`;

const DetailHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 0.85rem;
`;

const DetailTitle = styled.div`
  color: #e2e8f0;
  font-size: 0.92rem;
  font-weight: 600;
`;

const DetailHint = styled.div`
  color: #64748b;
  font-size: 0.72rem;
`;

const BreakdownTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
`;

const Th = styled.th`
  text-align: left;
  color: #64748b;
  font-weight: 600;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const Td = styled.td`
  color: #e2e8f0;
  padding: 0.55rem 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
`;

const TdRight = styled(Td)`
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const EmptyText = styled.div`
  color: #64748b;
  font-size: 0.8rem;
  font-style: italic;
  padding: 0.75rem 0;
`;

const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 4rem 1rem;
  color: #94a3b8;

  svg {
    width: 18px;
    height: 18px;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 3rem 1rem;
  color: #fca5a5;

  svg {
    width: 32px;
    height: 32px;
    margin-bottom: 0.6rem;
  }
`;

const formatCurrency = (n: number | null | undefined): string => {
  const v = typeof n === 'number' ? n : 0;
  if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
};

const PILLAR_CONFIG = {
  publicFI: { label: 'Public Fixed Income', color: '#3b82f6', icon: <TrendingUp /> },
  creditAndLoans: { label: 'Credit & Loans', color: '#f59e0b', icon: <Briefcase /> },
  alternatives: { label: 'Alternatives', color: '#8b5cf6', icon: <Layers /> },
};

// Map credit/alts breakdown keys to readable labels — order also drives row sequence.
const CREDIT_LABELS: Array<{ key: string; label: string }> = [
  { key: 'leveragedLoans', label: 'Leveraged Loans' },
  { key: 'privateCredit', label: 'Private Credit' },
  { key: 'cds', label: 'CDS Contracts' },
];

const ALTS_LABELS: Array<{ key: string; label: string }> = [
  { key: 'portfolioCompanies', label: 'Portfolio Companies' },
  { key: 'realEstate', label: 'Real Estate' },
  { key: 'privateEquity', label: 'Private Equity Funds' },
];

const IssuerProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<HoldingsByPillarResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const r = await axios.get<HoldingsByPillarResponse>(`/api/credit/issuers/${id}/holdings-by-pillar`);
        if (!cancelled) setData(r.data);
      } catch (e: any) {
        if (cancelled) return;
        if (e?.response?.status === 404) {
          setError('Issuer not found.');
        } else {
          setError(e?.response?.data?.message || 'Failed to load issuer profile.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (id) load();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <Container>
        <Loading>
          <Loader2 />
          Loading issuer profile…
        </Loading>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container>
        <TopBar>
          <BackBtn onClick={() => navigate(-1)}>
            <ArrowLeft />
            Back
          </BackBtn>
        </TopBar>
        <ErrorBox>
          <AlertCircle />
          {error || 'No data available.'}
        </ErrorBox>
      </Container>
    );
  }

  const { issuer, publicFI, creditAndLoans, alternatives } = data;
  const pillars: Array<{ key: keyof typeof PILLAR_CONFIG; data: PillarSummary }> = [
    { key: 'publicFI', data: publicFI },
    { key: 'creditAndLoans', data: creditAndLoans },
    { key: 'alternatives', data: alternatives },
  ];

  return (
    <Container>
      <TopBar>
        <BackBtn onClick={() => navigate(-1)}>
          <ArrowLeft />
          Back
        </BackBtn>
      </TopBar>

      <Header>
        <IssuerName>
          <Building2 />
          {issuer.issuerName}
        </IssuerName>
        <MetaRow>
          {issuer.sector && (
            <MetaItem>
              <span>Sector:</span>
              <strong>{issuer.sector}</strong>
            </MetaItem>
          )}
          {issuer.industry && (
            <MetaItem>
              <span>Industry:</span>
              <strong>{issuer.industry}</strong>
            </MetaItem>
          )}
          {issuer.country && (
            <MetaItem>
              <Globe />
              <strong>{issuer.country}</strong>
            </MetaItem>
          )}
          {issuer.creditRating && (
            <MetaItem>
              <Star />
              <strong>{issuer.creditRating}</strong>
            </MetaItem>
          )}
        </MetaRow>
      </Header>

      <SectionTitle>
        <Layers />
        Held in
      </SectionTitle>

      <PillarStrip>
        {pillars.map(({ key, data: p }) => {
          const cfg = PILLAR_CONFIG[key];
          const hasData = p.instrumentCount > 0;
          return (
            <PillarCard
              key={key}
              $accent={cfg.color}
              $hasData={hasData}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <PillarHead>
                <PillarIcon $color={cfg.color}>{cfg.icon}</PillarIcon>
                <PillarLabel>{cfg.label}</PillarLabel>
              </PillarHead>
              <PillarValue>{formatCurrency(p.marketValue)}</PillarValue>
              <PillarMeta>
                <span>
                  <strong>{p.instrumentCount}</strong> instrument{p.instrumentCount === 1 ? '' : 's'}
                </span>
                {p.portfolioCount != null && (
                  <span>
                    <strong>{p.portfolioCount}</strong> portfolio{p.portfolioCount === 1 ? '' : 's'}
                  </span>
                )}
              </PillarMeta>
            </PillarCard>
          );
        })}
      </PillarStrip>

      {/* ── Public FI breakdown ──────────────────────────────────────────── */}
      <Detail>
        <DetailHead>
          <DetailTitle>Public FI — by asset class</DetailTitle>
          <DetailHint>Sourced from fact_positions (live IBOR)</DetailHint>
        </DetailHead>
        {publicFI.byAssetClass && publicFI.byAssetClass.length > 0 ? (
          <BreakdownTable>
            <thead>
              <tr>
                <Th>Asset class</Th>
                <Th style={{ textAlign: 'right' }}>Instruments</Th>
                <Th style={{ textAlign: 'right' }}>Market value</Th>
              </tr>
            </thead>
            <tbody>
              {publicFI.byAssetClass.map((row) => (
                <tr key={row.assetClass}>
                  <Td>{row.assetClass}</Td>
                  <TdRight>{row.instrumentCount}</TdRight>
                  <TdRight>{formatCurrency(row.marketValue)}</TdRight>
                </tr>
              ))}
            </tbody>
          </BreakdownTable>
        ) : (
          <EmptyText>No public-FI positions for this issuer.</EmptyText>
        )}
      </Detail>

      {/* ── Credit & Loans breakdown ────────────────────────────────────── */}
      <Detail>
        <DetailHead>
          <DetailTitle>Credit &amp; Loans — by channel</DetailTitle>
          <DetailHint>Borrower-name match across credit channels</DetailHint>
        </DetailHead>
        <ChannelBreakdownTable
          breakdown={creditAndLoans.breakdown}
          labels={CREDIT_LABELS}
          emptyText="No credit / loan exposure to this issuer."
        />
      </Detail>

      {/* ── Alternatives breakdown ──────────────────────────────────────── */}
      <Detail>
        <DetailHead>
          <DetailTitle>Alternatives — by channel</DetailTitle>
          <DetailHint>Portco company-name match (RE / PE are property/fund-anchored)</DetailHint>
        </DetailHead>
        <ChannelBreakdownTable
          breakdown={alternatives.breakdown}
          labels={ALTS_LABELS}
          emptyText="No alternatives exposure to this issuer."
        />
      </Detail>
    </Container>
  );
};

const ChannelBreakdownTable: React.FC<{
  breakdown?: Record<string, PillarBreakdown>;
  labels: Array<{ key: string; label: string }>;
  emptyText: string;
}> = ({ breakdown, labels, emptyText }) => {
  if (!breakdown) return <EmptyText>{emptyText}</EmptyText>;
  const rows = labels
    .map((l) => ({ ...l, value: breakdown[l.key] }))
    .filter((r) => r.value && r.value.instrumentCount > 0);
  if (rows.length === 0) return <EmptyText>{emptyText}</EmptyText>;
  return (
    <BreakdownTable>
      <thead>
        <tr>
          <Th>Channel</Th>
          <Th style={{ textAlign: 'right' }}>Instruments</Th>
          <Th style={{ textAlign: 'right' }}>Exposure</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key}>
            <Td>{r.label}</Td>
            <TdRight>{r.value.instrumentCount}</TdRight>
            <TdRight>{formatCurrency(r.value.marketValue)}</TdRight>
          </tr>
        ))}
      </tbody>
    </BreakdownTable>
  );
};

export default IssuerProfile;
